import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import type { ApiRequestError } from '@/services/http/errors'
import { normalizeError } from '@/services/http/errors'

/**
 * 登入與註冊共用的提交流程。
 *
 * 兩個表單的欄位不同，但「提交中狀態、錯誤正規化、把後端欄位錯誤攤回輸入框」
 * 這段完全一樣。抽在這裡而不是各自複製 —— T1 的
 * `MemberLogin` / `MemberRegister` / `MemberSignInUp` 就是複製到失控。
 *
 * 內部一律保存 **i18n key** 而非翻譯後的字串，對外才用 computed 翻譯。
 * 這樣使用者在錯誤訊息顯示中切換語言時，訊息會跟著換 ——
 * 存成字串的話會卡在切換前的語言。
 */
export function useAuthForm() {
  const { t, te } = useI18n()

  const isSubmitting = ref(false)
  /** 整體錯誤的 i18n key，顯示在表單頂端 */
  const formErrorKey = ref<string | null>(null)
  /** 個別欄位錯誤的 i18n key，key 是欄位名 */
  const fieldErrorKeys = ref<Record<string, string>>({})

  /** i18n key 不存在時退回泛用訊息，避免畫面直接吐出 raw key */
  function translate(key: string, fallback: string): string {
    return te(key) ? t(key) : t(fallback)
  }

  const formError = computed(() =>
    formErrorKey.value === null ? null : translate(formErrorKey.value, 'errors.UNKNOWN'),
  )

  const fieldErrors = computed<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(fieldErrorKeys.value).map(([field, key]) => [
        field,
        translate(key, 'errors.VALIDATION_FAILED'),
      ]),
    ),
  )

  function reset(): void {
    formErrorKey.value = null
    fieldErrorKeys.value = {}
  }

  /** 本地驗證的結果，傳入的是 i18n key（例如 `validation.USERNAME_FORMAT`） */
  function setFieldErrors(keys: Record<string, string>): void {
    fieldErrorKeys.value = keys
    formErrorKey.value = null
  }

  function applyError(error: ApiRequestError): void {
    const fields = error.fieldErrors()
    fieldErrorKeys.value = fields

    // 有逐欄位訊息時就不再顯示頂端的泛用錯誤，避免重複轟炸
    formErrorKey.value = Object.keys(fields).length > 0 ? null : error.i18nKey
  }

  /**
   * 包住一次提交。回傳 `true` 代表成功。
   * 呼叫端只需要關心「成功後要去哪」，錯誤處理都在這裡。
   */
  async function submit(action: () => Promise<void>): Promise<boolean> {
    if (isSubmitting.value) return false

    reset()
    isSubmitting.value = true

    try {
      await action()
      return true
    } catch (error) {
      applyError(normalizeError(error))
      return false
    } finally {
      isSubmitting.value = false
    }
  }

  return { isSubmitting, formError, fieldErrors, setFieldErrors, submit, reset }
}
